import { createContext, useContext, useEffect, useRef, useState } from "react";

import {
  type ModalHeaderProps,
  type ModalProps,
  Modal as SemanticModal,
} from "semantic-ui-react";

import { uuidv4 } from "simplydo/core/helpers";

import {
  FocusStackContext,
  createFocusTrap,
  useFocusStack,
} from "./utils/FocusStack";

const ModalContext = createContext({ titleId: "" });

const Modal = (props: ModalProps) => {
  const focusStack = useFocusStack();

  const modalRef = useRef<HTMLDivElement>(null);
  const [returnFocusElement, setReturnFocusElement] =
    useState<HTMLElement | null>(null);

  const [internalOpen, setInternalOpen] = useState(props.open ?? false);
  const [titleId] = useState(uuidv4());

  useEffect(() => {
    if (
      props.open !== undefined ? props.open === true : internalOpen === true
    ) {
      setReturnFocusElement(document.activeElement as HTMLElement);

      const focusTrap = createFocusTrap(modalRef.current);
      focusStack.insertFocusTrap(focusTrap);

      return () => {
        focusStack.removeFocusTrap(focusTrap);
      };
    }
  }, [props.open, focusStack, internalOpen]);

  useEffect(() => {
    if (
      props.open !== undefined ? props.open === false : internalOpen === false
    ) {
      if (returnFocusElement && document.activeElement !== returnFocusElement) {
        returnFocusElement.focus();
      }
    }
  }, [props.open, internalOpen, returnFocusElement]);

  return (
    <FocusStackContext.Provider value={focusStack}>
      <ModalContext.Provider value={{ titleId }}>
        <SemanticModal
          role="dialog"
          aria-modal="true"
          mountNode={document.getElementById("semantic-modal-mount-node")}
          aria-labelledby={titleId}
          tabIndex={-1}
          closeOnEscape
          ref={modalRef}
          {...props}
          onOpen={(e, data) => {
            setInternalOpen(true);
            props.onOpen?.(e, data);
          }}
          onClose={(e, data) => {
            setInternalOpen(false);
            props.onClose?.(e, data);
          }}
        />
      </ModalContext.Provider>
    </FocusStackContext.Provider>
  );
};

const ModalHeader = (props: ModalHeaderProps) => {
  const { titleId } = useContext(ModalContext);
  return <SemanticModal.Header id={titleId} {...props} />;
};

Modal.Header = ModalHeader;
Modal.Content = SemanticModal.Content;
Modal.Actions = SemanticModal.Actions;
Modal.Dimmer = SemanticModal.Dimmer;
Modal.Description = SemanticModal.Description;

export { Modal };
